import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import RichTextEditor from '@/components/admin/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterComposer = ({ open, onOpenChange, subscriberCount, onSent }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(null);

  const send = async (testOnly) => {
    if (!subject.trim() || !content.trim()) {
      toast.error('Subject and content are required');
      return;
    }
    if (!testOnly && !window.confirm(`Send this newsletter to all ${subscriberCount} subscriber(s)?`)) return;
    setSending(testOnly ? 'test' : 'all');
    try {
      const { data } = await axios.post(
        `${API}/admin/newsletter/send`,
        { subject, content, test_only: testOnly },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      toast.success(data.message);
      if (!testOnly) {
        setSubject('');
        setContent('');
        onOpenChange(false);
        onSent?.();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Sending failed');
    } finally {
      setSending(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="newsletter-composer-modal">
        <DialogHeader>
          <DialogTitle>Compose Newsletter</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. 5 Leadership Hiring Trends to Watch This Quarter"
              data-testid="newsletter-subject"
              className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content *</label>
            <RichTextEditor value={content} onChange={setContent} placeholder="Write your newsletter..." />
            <p className="text-xs text-muted-foreground mt-2">
              Sent inside the branded Vocavi template with header, contact footer and a one-click unsubscribe link.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={!!sending}
              onClick={() => send(true)}
              data-testid="newsletter-send-test"
            >
              <FlaskConical size={16} className="mr-2" /> {sending === 'test' ? 'Sending test...' : 'Send test to admin'}
            </Button>
            <Button
              type="button"
              disabled={!!sending || subscriberCount === 0}
              onClick={() => send(false)}
              data-testid="newsletter-send-all"
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Send size={16} className="mr-2" /> {sending === 'all' ? 'Sending...' : `Send to all ${subscriberCount} subscriber(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterComposer;
